import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '../../../modules/core/store/store.model';
import { combineLatest, interval, map, startWith, takeUntil, Subject } from 'rxjs';
import { TrafficDeviationReceipt, TrafficDeviationTypesUnion } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-union';
import { TrafficDeviationTypeDeviationInformation } from '@modules/shared/interfaces/traffic-deviations/traffic-deviation-type-deviation-information';

interface TrafficDeviationsState {
    deviationScope: 'DEFAULT' | 'TRIP' | 'COMING_TRIP';
    trafficDeviations: ReadonlyArray<TrafficDeviationTypesUnion>;
    trafficDeviationReceipts: ReadonlyArray<TrafficDeviationReceipt>;
    expiredTrafficDeviations: ReadonlyArray<TrafficDeviationTypeDeviationInformation>;
    selectedDeviationType: TrafficDeviationTypesUnion['type'];
    informationTemplates: ReadonlyArray<InformationTemplate>;
    automaticVariables: ReadonlyArray<string>;
    automaticRouteInfoVariable: ReadonlyArray<string>;
    manualVariables: ReadonlyArray<string>;
    savedPreviewEditForm: PreviewEditForm;
}

export interface PreviewEditForm {
    id: string;
    version: number;
    name: string;
    summary: string;
    header: string;
    content: string;
    deviationTypes: {
        DeviationInformation: boolean;
        ChangeOfJourneyPattern: boolean;
        ChangeOfJourneyTiming: boolean;
        PredictionInaccurate: boolean;
    };
    modifiedDate: string;
    modifiedBy: string;
    unchangedForm: Omit<PreviewEditForm, 'unchangedForm'>;
}

export interface InformationTemplate {
    id: string;
    version: number;
    deviationTypes: Array<TrafficDeviationTypesUnion['type']>;
    name: string;
    summary: string;
    header: string;
    content: string;
    modifiedDate: string;
    modifiedBy: string;
    automaticDescription?: string;
    defaultTemplate?: boolean;
    automaticTemplate?: boolean;
}

const initialState: TrafficDeviationsState = {
    deviationScope: 'DEFAULT',
    trafficDeviations: [],
    trafficDeviationReceipts: [],
    expiredTrafficDeviations: [],
    selectedDeviationType: undefined,
    informationTemplates: [], 
    automaticVariables: [
        'Cause', 'Start date', 'Start date and Time', 'End date and Time', 'End date', 'Minutes'
    ],
    automaticRouteInfoVariable: [
        'Routenumber', 'Stops', 'Stop name', 'Retracted Stops', 'Routenumber to Direction', 
        'First retracted to last retracted stop', 'New stops', 'Trips Start Stop', 
        'Trips End Stop', 'Trips Start Time'
    ],
    manualVariables: [
        'CustomFieldDescription', 'Amount of Minutes', 'Departures', 'Date and Time', 'Time'
    ],
    savedPreviewEditForm: undefined
};

@Injectable({
    providedIn: 'root'
})
export class TrafficDeviationsStore extends Store<TrafficDeviationsState> implements OnDestroy {
    private readonly destroy$ = new Subject<void>();
    public readonly deviationScope$ = this.select((store) => store.deviationScope);
    public readonly trafficDeviations$ = this.select((store) => store.trafficDeviations);
    public readonly trafficDeviationReceipts$ = this.select((store) => store.trafficDeviationReceipts);
    public readonly expiredTrafficDeviations$ = this.select((store) => store.expiredTrafficDeviations);
    public readonly selectedDeviationType$ = this.select((store => store.selectedDeviationType));
    public readonly informationTemplates$ = this.select((store) => store.informationTemplates).pipe(
        map((templates) => [...templates].sort((a, b) => a.name.localeCompare(b.name)))
    );
    public readonly automaticVariables$ = this.select((store => store.automaticVariables));
    public readonly automaticRouteInfoVariable$ = this.select((store => store.automaticRouteInfoVariable));
    public readonly manualVariables$ = this.select((store => store.manualVariables));
    public readonly savedPreviewEditForm$ = this.select((store => store.savedPreviewEditForm));
    public readonly deviationPutSuccess$ = new Subject<void>();

    constructor() { 
        super(initialState);
        this._startCleanupTimer();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public updateTrafficDeviationState(state: Partial<TrafficDeviationsState>): void {
        this.patchState(state);
    }

    public async updateTrafficDeviation(deviations: ReadonlyArray<TrafficDeviationTypesUnion>) {
        if(!deviations?.length) return;
        
        const state = { ...this.stateSnapshot };
        for (const message of deviations) {
            state.trafficDeviations = this._updateTrafficDeviations(state.trafficDeviations, message);
        }

        this.setState(state);
    }

    public async updateTrafficDeviationReceipt(receipts: ReadonlyArray<TrafficDeviationReceipt>) {
        if(!receipts?.length) return;

        const state = { ...this.stateSnapshot };
        for (const message of receipts) {
            state.trafficDeviationReceipts = this._updateTrafficDeviationReceipts(state.trafficDeviationReceipts, message);
        }

        this.setState(state);
    }

    public setExpiredTrafficDeviations(deviations: ReadonlyArray<TrafficDeviationTypeDeviationInformation>): void {
        this.patchState({ expiredTrafficDeviations: deviations });
    }

    public getTrafficDeviationOnId(id: string) {
        return combineLatest([
            this.trafficDeviations$,
            this.expiredTrafficDeviations$,
        ]).pipe(
            map(([active, expired]) =>
                active.find((d) => d.id === id) ?? expired.find((d) => d.id === id)
            )
        );
    }

    public remove(garageCode: string) {
        if (this.stateSnapshot.trafficDeviations) {
            const updatedDeviations = this.stateSnapshot.trafficDeviations.filter(td => td.garageCode !== garageCode);

            this.setState({
                ...this.stateSnapshot,
                trafficDeviations: updatedDeviations
            });
        }
    }

    private _startCleanupTimer(): void {
        interval(60000).pipe(
            startWith(0),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this._removeExpiredDeviations();
        });
    }

    private _removeExpiredDeviations(): void {
        const now = new Date();
        const currentDeviations = this.stateSnapshot.trafficDeviations;
        
        const activeDeviations = currentDeviations.filter(dev => 
            new Date(dev.trafficDeviationScope.endDate) > now
        );

        if (activeDeviations.length !== currentDeviations.length) {
            this.setState({
                ...this.stateSnapshot,
                trafficDeviations: activeDeviations
            });
        }
    }

    private _updateTrafficDeviations(
        trafficDeviations: ReadonlyArray<TrafficDeviationTypesUnion>, 
        deviationMessage: TrafficDeviationTypesUnion
    ): ReadonlyArray<TrafficDeviationTypesUnion> | undefined {
        let findExistingTrafficDeviationIndex = trafficDeviations.findIndex((d) => d.id === deviationMessage.id);

        // existing traffic deviation and not deleted, update!
        if (findExistingTrafficDeviationIndex !== -1 && !deviationMessage.isDeleted) {
            const updatedTrafficDeviations = [...trafficDeviations];
            updatedTrafficDeviations.splice(findExistingTrafficDeviationIndex, 1, deviationMessage);
            return updatedTrafficDeviations;
        }
        // existing traffic deviation and deleted, remove!
        if (findExistingTrafficDeviationIndex !== -1 && deviationMessage.isDeleted) {
            const updatedTrafficDeviations = [...trafficDeviations];
            updatedTrafficDeviations.splice(findExistingTrafficDeviationIndex, 1);
            return updatedTrafficDeviations;
        }
        // new traffic deviation but deleted, don't add!
        if (findExistingTrafficDeviationIndex === -1 && deviationMessage.isDeleted) {
            return trafficDeviations;
        }
        
        // new traffic deviation and not deleted, add!
        return [...trafficDeviations, deviationMessage];
    }

    private _updateTrafficDeviationReceipts(
        receipts: ReadonlyArray<TrafficDeviationReceipt>, 
        receiptMessage: TrafficDeviationReceipt
    ): ReadonlyArray<TrafficDeviationReceipt> | undefined {
        let findExistingTrafficDeviationIndex = receipts.findIndex((d) => d.trafficDeviationId === receiptMessage.trafficDeviationId);

        // existing traffic deviation and not deleted, update!
        if (findExistingTrafficDeviationIndex !== -1) {
            const updatedTrafficDeviations = [ 
                ...receipts.slice(0, findExistingTrafficDeviationIndex), 
                receiptMessage, 
                ...receipts.slice(Math.min(...[receipts.length - 1, findExistingTrafficDeviationIndex + 1])) 
            ];

            return updatedTrafficDeviations;
        }
        
        // new traffic deviation and not deleted, add!
        return [...receipts, receiptMessage];
    }
}
